import { Box, Button, makeStyles, styled } from "@material-ui/core";
import {
  CheckCircle,
  FileCopyOutlined,
  ReportProblemOutlined,
} from "@material-ui/icons";
import { memo, useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_SYNCED_BG,
  EPOCUS_SYNCED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type { WatchOutcome } from "../onboarding-chat";
import type { WatchProgress } from "../use-onboarding-chat";

const Card = styled(Box)({
  marginLeft: 38,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const Heading = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: rem(14),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

// MUI v4 `styled()` compiles a single rule; a nested @keyframes throws in JSS.
const usePulseStyles = makeStyles({
  "@keyframes epocusPulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.35, transform: "scale(0.7)" },
  },
  pulse: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: EPOCUS_PRIMARY,
    animation: "$epocusPulse 1.8s ease-in-out infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
});

const Body = styled("p")({
  margin: 0,
  fontSize: rem(13),
  lineHeight: 1.55,
  color: EPOCUS_TEXT_PRIMARY,
});

const Muted = styled("p")({
  margin: 0,
  fontSize: rem(12),
  lineHeight: 1.5,
  color: EPOCUS_TEXT_SECONDARY,
});

const AeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
});

const AeTitle = styled("code")({
  fontFamily: "monospace",
  fontSize: rem(18),
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: EPOCUS_TEXT_PRIMARY,
});

const CopyButton = styled(Button)({
  minHeight: 44,
  textTransform: "none",
  fontSize: rem(13),
});

const Actions = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

const CheckNowButton = styled(Button)({
  minHeight: 44,
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 10,
});

const Meter = styled(Box)({
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  fontVariantNumeric: "tabular-nums",
});

// MUI v4 `styled` forwards every prop to the DOM, so tone cannot be a prop.
const Outcome = styled(Box)({
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  borderRadius: 10,
  padding: 12,
  fontSize: rem(13),
  lineHeight: 1.5,
});

const TONE_STYLES = {
  good: { backgroundColor: EPOCUS_SYNCED_BG, color: EPOCUS_SYNCED_TEXT },
  bad: {
    backgroundColor: EPOCUS_STATUS_DRAFT_BG,
    color: EPOCUS_STATUS_DRAFT_TEXT,
  },
} as const;

const formatElapsed = (ms: number) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const OUTCOME_COPY: Record<
  WatchOutcome,
  { tone: "good" | "bad"; title: string; body: string }
> = {
  ROUTED: {
    tone: "good",
    title: "Verified",
    body: "The test study arrived and was routed into this program. Scans from this device will reach the right people.",
  },
  UNROUTED: {
    tone: "bad",
    title: "Arrived, but not routed",
    body: "A study reached the gate but did not match this program's AE title, so it landed unattributed and nobody would see it.",
  },
  RECEIVED_NOT_PROCESSED: {
    tone: "bad",
    title: "Received, not processed yet",
    body: "The gate accepted the study but the middleware has not finished with it. This usually clears on its own — worth another look shortly.",
  },
  NOT_RECEIVED: {
    tone: "bad",
    title: "Nothing arrived",
    body: "No study reached the gate at all. That points at the device configuration or the network path rather than at the program.",
  },
};

export const WatchCard = memo<{
  instructions: string;
  progress?: WatchProgress;
  onCheckNow: () => void;
}>(({ instructions, progress, onCheckNow }) => {
  const classes = usePulseStyles();
  const [copied, setCopied] = useState(false);
  const aeTitle = instructions.match(/\b[A-Z0-9_]{4,16}\b/)?.[0];
  const outcome = progress?.outcome
    ? OUTCOME_COPY[progress.outcome]
    : undefined;

  const copy = async () => {
    if (!aeTitle) return;
    try {
      await navigator.clipboard.writeText(aeTitle);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card role="status" aria-live="polite">
      {outcome ? (
        <Outcome style={TONE_STYLES[outcome.tone]}>
          {outcome.tone === "good" ? (
            <CheckCircle style={{ fontSize: 20 }} />
          ) : (
            <ReportProblemOutlined style={{ fontSize: 20 }} />
          )}
          <Box>
            <Box fontWeight={700}>{outcome.title}</Box>
            <Box>{outcome.body}</Box>
            {progress?.detail && <Box mt={0.5}>{progress.detail}</Box>}
          </Box>
        </Outcome>
      ) : (
        <>
          <Heading>
            <span className={classes.pulse} aria-hidden />
            Waiting for a scan to arrive
          </Heading>
          <Body>{instructions}</Body>
          {aeTitle && (
            <AeRow>
              <AeTitle>{aeTitle}</AeTitle>
              <CopyButton
                size="small"
                startIcon={<FileCopyOutlined style={{ fontSize: 16 }} />}
                onClick={() => void copy()}
              >
                {copied ? "Copied" : "Copy"}
              </CopyButton>
            </AeRow>
          )}
          <Actions>
            <CheckNowButton
              variant="outlined"
              color="primary"
              disableElevation
              onClick={onCheckNow}
            >
              I've set it — check now
            </CheckNowButton>
          </Actions>
          <Meter>
            <span>{`Waiting ${formatElapsed(progress?.elapsedMs ?? 0)}`}</span>
            <span>
              {!progress?.attempt
                ? "Checking shortly"
                : `Checked ${progress.attempt} ${progress.attempt === 1 ? "time" : "times"}`}
            </span>
          </Meter>
          <Muted>
            This takes as long as it takes someone to reach the machine. Keep
            this page open — the check stops if you leave, and the conversation
            starts again from the top.
          </Muted>
        </>
      )}
    </Card>
  );
});
WatchCard.displayName = "WatchCard";
